import { z } from 'zod';
/**
 * The branch-name contract that ties a git branch to a ticket, and the
 * environment lane a ticket's code travels along.
 *
 * Everything downstream — auto-linking PRs, the "where is my code" pipeline on
 * the ticket, and the automatic status transitions — keys off the ticket code
 * appearing in the branch name. Getting that code in there is therefore the
 * ONE thing a developer (or an AI agent) has to do by hand, so the rule lives
 * here, is shared verbatim by the API and the web app, and is surfaced to
 * agents through the agent API.
 *
 *   <prefix>/<TICKET-CODE>-<slug>
 *   feat/ATL-000123-image-lightbox
 *
 * Matching is deliberately lenient (see BRANCH_CODE_RE): the code may appear
 * anywhere in the branch, in any case, so `ATL-000123`, `bugfix/atl-123-x` and
 * `ATL-000123` in a PR title all resolve. Only the *generated* name is strict.
 */
/** Branch prefixes offered by the copy-branch helper and accepted by the PR check. */
export const DEFAULT_BRANCH_PREFIXES = ['feat', 'fix', 'chore', 'refactor', 'docs', 'test', 'hotfix'];
/** Ticket-type → the prefix that best describes the work. */
export const BRANCH_PREFIX_BY_TYPE = {
    internal_bug: 'fix',
    customer_support: 'fix',
    feature_request: 'feat',
    change_request: 'feat',
    task: 'chore',
    epic: 'feat',
    subtask: 'feat',
};
/**
 * Ticket codes as they appear inside a branch name, PR title, or commit
 * message. Case-insensitive so `feat/atl-000123-x` still links; the captured
 * code is upper-cased by the caller before lookup.
 *
 * NOTE: this is intentionally a superset of the strict generated form — humans
 * type `ATL-123`, the generator emits `ATL-000123`, and both must resolve.
 */
export const BRANCH_CODE_RE = /\b([A-Za-z]{2,}-\d{1,6})\b/g;
/** Trim a ticket title into the `-slug` tail of a branch name. */
export function slugifyForBranch(title, maxLength = 48) {
    const slug = title
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    if (slug.length <= maxLength)
        return slug;
    // Cut at the last whole word inside the budget so we never end mid-token.
    const cut = slug.slice(0, maxLength);
    const lastDash = cut.lastIndexOf('-');
    return (lastDash > 0 ? cut.slice(0, lastDash) : cut).replace(/-+$/, '');
}
/** The canonical branch name for a ticket, e.g. `feat/ATL-000123-image-lightbox`. */
export function buildBranchName(ticket) {
    const prefix = BRANCH_PREFIX_BY_TYPE[ticket.type ?? ''] ?? 'feat';
    const slug = slugifyForBranch(ticket.title);
    return slug ? `${prefix}/${ticket.code}-${slug}` : `${prefix}/${ticket.code}`;
}
/** The ready-to-paste command that creates and checks out that branch. */
export function buildCheckoutCommand(branch, baseBranch = 'develop') {
    return `git checkout ${baseBranch} && git pull && git checkout -b ${branch}`;
}
/** Every ticket code referenced in the given texts, upper-cased and deduped. */
export function extractTicketCodes(...texts) {
    const found = new Set();
    for (const t of texts) {
        if (!t)
            continue;
        for (const m of t.matchAll(BRANCH_CODE_RE))
            found.add(m[1].toUpperCase());
    }
    return [...found];
}
// ── Environment lane ────────────────────────────────────────────────────────
/** Deployment lanes a ticket's code passes through, in order. */
export const DevEnvironment = z.enum(['dev', 'uat', 'staging']);
/** What causes a branch rule to fire: the PR merging, or a confirmed deploy. */
export const BranchRuleTrigger = z.enum(['merge', 'deploy']);
/**
 * One mapping from a base branch to the environment it represents and the
 * ticket status that code landing there implies.
 */
export const BranchRule = z.object({
    /** Base branch on GitHub, e.g. `develop`. */
    branch: z.string().min(1),
    environment: DevEnvironment,
    /** Human label shown in the ticket pipeline, e.g. "DAV". */
    label: z.string().min(1),
    /** Status the ticket advances to. Must be a real TicketStatus. */
    status: z.string().min(1),
    /** `merge` fires when the PR merges; `deploy` waits for a confirmed deploy. */
    trigger: BranchRuleTrigger.default('merge'),
    enabled: z.boolean().default(true),
});
/**
 * Tracx's lane. `develop` is the DAV environment; UAT has its own branch;
 * `main` is Staging. Nothing here ever sets `done` — a human confirms that
 * after checking staging.
 */
export const DEFAULT_BRANCH_RULES = [
    { branch: 'develop', environment: 'dev', label: 'DAV', status: 'qa', trigger: 'merge', enabled: true },
    { branch: 'uat', environment: 'uat', label: 'UAT', status: 'uat', trigger: 'merge', enabled: true },
    { branch: 'main', environment: 'staging', label: 'Staging', status: 'staging', trigger: 'merge', enabled: true },
];
export const GithubConfigPayload = z.object({
    automationEnabled: z.boolean(),
    branchRules: z.array(BranchRule),
    prOpenedStatus: z.string().nullable(),
    revertOnClosedPr: z.boolean(),
    branchPrefixes: z.array(z.string()),
});
/** Why an automation decision did or did not change a ticket. Surfaced in
 *  Admin → Integrations → GitHub so a "why didn't my ticket move?" question has
 *  a concrete answer instead of silence. */
export const AutomationOutcome = z.enum([
    'applied',
    'automation_disabled',
    'no_rule_for_branch',
    'rule_disabled',
    'ticket_not_found',
    'not_forward_progress',
    'transition_not_allowed',
    'already_in_status',
    'awaiting_deploy',
    'error',
]);
//# sourceMappingURL=dev-pipeline.js.map